import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/site";
import { shopApi } from "@/lib/shopApi";
import { CreatorAvatar, VerifiedMark } from "@/components/stage/PostCard";
import { ProductCard } from "@/pages/shop/Shop";

export function Store() {
  const { sellerId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(undefined);
  const s = data?.seller;
  usePageMeta(s ? `${s.business_name || s.display_name} · Store` : "Store", "A creator's store on Fable +.");
  useEffect(() => {
    let alive = true;
    setData(undefined);
    shopApi.store(sellerId).then((d) => alive && setData(d)).catch((e) => alive && setData({ error: e?.status === 404 ? "This store doesn't exist." : e?.message || "Couldn't load this store." }));
    return () => { alive = false; };
  }, [sellerId, user?.id]);

  if (data === undefined) return <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6"><Skeleton className="h-24 rounded-2xl" /><div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />)}</div></div>;
  if (data.error) return <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6"><p className="font-semibold">{data.error}</p><Link to="/shop"><Button variant="outline" className="mt-5 rounded-full"><ArrowLeft size={16} className="mr-1.5" /> Shop</Button></Link></div>;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid={`store-${sellerId}`}>
      <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Shop</Link>
      <div className="mt-4 flex items-center gap-4 rounded-2xl border border-border bg-card p-5">
        <CreatorAvatar author={s} size={64} />
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-1.5 font-serif-display text-2xl font-bold">{s.business_name || s.display_name} <VerifiedMark author={s} size={18} /></h1>
          <p className="text-sm text-muted-foreground">{s.handle ? `@${s.handle} · ` : ""}{s.sold || 0} sold · {data.items.length} product{data.items.length === 1 ? "" : "s"}</p>
        </div>
        {s.handle && <Link to={`/@${s.handle}`}><Button variant="outline" className="rounded-full">Profile</Button></Link>}
      </div>
      {data.items.length === 0 ? (
        <p className="mt-10 text-center text-muted-foreground">Nothing for sale right now.</p>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="store-grid">{data.items.map((p) => <ProductCard key={p.id} p={p} />)}</div>
      )}
    </div>
  );
}

const STATUS = { paid: ["Paid", "bg-sky-100 text-sky-800"], fulfilled: ["Posted", "bg-emerald-100 text-emerald-800"], refunded: ["Refunded", "bg-muted text-foreground/70"], pending_payment: ["Pending", "bg-amber-100 text-amber-800"] };

export function Orders() {
  usePageMeta("My orders", "Your Fable + shop orders.");
  const [data, setData] = useState(undefined);
  useEffect(() => {
    let alive = true;
    shopApi.myOrders().then((d) => alive && setData(d.items || [])).catch(() => alive && setData(null));
    return () => { alive = false; };
  }, []);
  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6" data-testid="shop-orders">
      <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Shop</Link>
      <h1 className="mt-3 font-serif-display text-3xl font-bold">My orders</h1>
      {data === undefined ? (
        <div className="mt-6 space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : data === null ? (
        <p className="mt-6 text-muted-foreground">Couldn't load your orders right now.</p>
      ) : data.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-border p-10 text-center">
          <Package size={24} className="mx-auto text-muted-foreground" />
          <p className="mt-2 font-semibold">No orders yet</p>
          <Link to="/shop"><Button className="mt-4 rounded-full">Browse the shop</Button></Link>
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {data.map((o) => {
            const [label, tone] = STATUS[o.status] || [o.status, "bg-muted"];
            return (
              <li key={o.id} className="flex items-center gap-4 rounded-2xl border border-border bg-card p-3" data-testid={`order-${o.id}`}>
                <Link to={`/shop/${o.product_id}`} className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-muted">{o.product_cover && <img src={o.product_cover} alt="" className="h-full w-full object-cover" />}</Link>
                <div className="min-w-0 flex-1">
                  <Link to={`/shop/${o.product_id}`} className="truncate font-semibold hover:underline">{o.product_title}</Link>
                  <p className="text-xs text-muted-foreground">{o.qty} × · {o.seller?.display_name || "Seller"}{o.paid_at ? ` · ${new Date(o.paid_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono-num font-semibold">£{Number(o.amount_gbp || 0).toFixed(2)}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{label}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
